import React, { useEffect, useState } from "react";

import styled, { keyframes } from "styled-components";

import {
  BackBtn,
  ButtonGroup,
  PrimaryBtn,
  SectionLabel,
  SlidePanelBody,
  StepHeaderWrap,
} from "./styles";

/* Shared phone-modal-template pieces (docu/modal-template-phone.md) for the cart
   checkout steps. Each step renders:
     <PhoneHeader><h2>Title</h2></PhoneHeader>
     <ScrollArea>
       <Body ref={bodyRef}>
         <DesktopHeader>…icon-circle + title + subtitle…</DesktopHeader>
         <FirstSectionLabel>…first section…</FirstSectionLabel>
         …rest…
       </Body>
       <BottomFade $visible={canScrollDown}><ScrollHint><span>Scroll</span><ScrollChevron/></ScrollHint></BottomFade>
     </ScrollArea>
     <SlidePanelFooter><FooterActions>…Back + primary…</FooterActions></SlidePanelFooter>
   driven by `const canScrollDown = useScrollAffordance(bodyRef)`. */

/* PHONE-ONLY fixed header: centred title, no icon, symmetric ✕ spacing (16px
   above the ✕ + 36px ✕ + 16px below = min-height 68; side-only padding). Hidden
   ≥601px so desktop/tablet keep the centred in-body icon-circle header.
   `data-slide-header` lets CartModal's measure() account for it. */
export const PhoneHeader = styled.div.attrs({ "data-slide-header": "" })`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-height: 68px;
    padding: 0 24px;

    h2 {
      font-family: "Cormorant Garamond", serif;
      font-size: 1.5rem;
      font-weight: 400;
      color: #fff;
      margin: 0;
      line-height: 1.2;
      text-align: center;
    }
  }
`;

/* The original centred icon-circle header — desktop/tablet only. */
export const DesktopHeader = styled(StepHeaderWrap)`
  @media (max-width: 600px) {
    display: none;
  }
`;

/* Scroll body with a phone top-trim so the first section hugs the fixed header
   (the header's adjacency rule can't reach it through the ScrollArea wrapper). */
export const Body = styled(SlidePanelBody)`
  @media (max-width: 600px) {
    padding-top: 0;
  }
`;

/* First section label hugs the fixed header on phone (the shared 18px top margin
   is for between-section gaps, not the first one). */
export const FirstSectionLabel = styled(SectionLabel)`
  @media (max-width: 600px) {
    margin-top: 2px;
  }
`;

/* Footer: short secondary (Back) sized to its content width, primary flexes to
   fill — so a longer primary label ("Continue to Payment") stays on one line
   instead of wrapping (which would collapse the footer height on a busy swap).
   Desktop keeps the plain 50/50. */
export const FooterActions = styled(ButtonGroup)`
  margin-top: 0;
  border-top: none;
  padding-top: 0;

  @media (max-width: 600px) {
    ${BackBtn} {
      flex: 0 0 auto;
      width: auto;
      padding-left: 20px;
      padding-right: 20px;
    }
    ${PrimaryBtn} {
      flex: 1;
      width: auto;
      white-space: nowrap;
    }
  }
`;

/* Scroll affordance — iOS doesn't render the custom scrollbar for touch
   overflow, so a tall body can cut off at a clean line that reads as "the end".
   A bottom fade + a bouncing "Scroll ⌄" appear only while there's more below. */
export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const scrollHintAnim = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
`;

export const BottomFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 52px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  background: linear-gradient(to bottom, rgba(111, 83, 60, 0) 0%, rgba(111, 83, 60, 0.8) 52%, rgba(111, 83, 60, 1) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
`;

export const ScrollHint = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ebc28e;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
  animation: ${scrollHintAnim} 1.4s ease-in-out infinite;

  span {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ScrollChevron: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/* Drives the bottom fade + "Scroll ⌄" hint. The affordance only exists to tell
   the user the content is scrollable, so it shows **only while they're at the
   top** (and there's more below) and **hides the moment they scroll down** —
   once they've scrolled, they know it scrolls, so the cue gets out of the way.
   It **reappears whenever they scroll back to the top.** Recomputes on scroll,
   resize, and content/attribute changes (late-mounting content like a Stripe
   card element). */
export function useScrollAffordance(
  ref: React.RefObject<HTMLElement | null>,
  /* Pass any values that gate when the scroll element actually mounts (e.g.
     a modal's `mounted` flag). The effect re-runs when they change, so the
     listeners attach once the node appears — modals that `return null` until
     `mounted` is true would otherwise capture a null ref on first render and
     never re-attach (the ref object itself never changes). */
  extraDeps: React.DependencyList = [],
): boolean {
  const [canScrollDown, setCanScrollDown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) { setCanScrollDown(false); return; }
    let raf = 0;
    const update = () => {
      // Show only at the top (with more below); hide once scrolled down, and
      // show again whenever the user returns to the top.
      const atTop = el.scrollTop <= 10;
      const moreBelow = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
      setCanScrollDown(atTop && moreBelow);
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    const mo = new MutationObserver(schedule);
    mo.observe(el, { childList: true, subtree: true, attributes: true });
    window.addEventListener("resize", schedule);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...extraDeps]);
  return canScrollDown;
}
